import { factory, cruduex, am } from './utils';

import httpClient from '@/utils/httpClient';

const IMPORT = am(`IMPORT`);
const ADD_TAG = am(`ADD_TAG`);
const REMOVE_TAG = am(`REMOVE_TAG`);
const EDIT_FIELDS = am(`EDIT_FIELDS`);

export default {
  namespaced: true,
  ...factory({
    state: {
      isAddTagPending: false,
      isRemoveTagPending: false,
      isEditFieldsPending: false
    },
    mutations: {
      [ADD_TAG.STARTED](state) {
        state.isAddTagPending = true;
      },
      [ADD_TAG.COMPLETED](state, { applicationId, tag }) {
        const application = state.collection.find((item) => item.source.id === applicationId);
        if (application) {
          application.tags = application.tags || [];
          application.tags.push(tag);

          state.aggs.tags = state.aggs.tags || {};
          if (typeof state.aggs.tags[tag.name] === 'undefined') {
            state.aggs.tags = {
              ...state.aggs.tags,
              [tag.name]: 1
            };
          } else {
            state.aggs.tags[tag.name]++;
          }
        }
        state.isAddTagPending = false;
      },
      [ADD_TAG.FAILED](state) {
        state.isAddTagPending = false;
      },
      [REMOVE_TAG.STARTED](state) {
        state.isRemoveTagPending = true;
      },
      [REMOVE_TAG.COMPLETED](state, { applicationId, tag }) {
        const application = state.collection.find((item) => item.source.id === applicationId);
        if (application) {
          const index = application.tags.findIndex((item) => item.name === tag.name);
          if (index > -1) {
            application.tags.splice(index, 1);

            if (state.aggs.tags && state.aggs.tags[tag.name]) {
              state.aggs.tags[tag.name] = state.aggs.tags[tag.name] - 1;
            }

            if (state.aggs.tags[tag.name] <= 0) {
              delete state.aggs.tags[tag.name];
              state.aggs.tags = {
                ...state.aggs.tags
              };
            }
          }
        }
        state.isRemoveTagPending = false;
      },
      [REMOVE_TAG.FAILED](state) {
        state.isRemoveTagPending = false;
      },
      [EDIT_FIELDS.STARTED](state) {
        state.isEditFieldsPending = true;
      },
      [EDIT_FIELDS.COMPLETED](state, { applicationId, fields }) {
        const application = state.collection.findIndex((item) => item.source.id === applicationId);
        if (application > -1) {
          state.collection[application] = {
            ...state.collection[application],
            ...fields
          };
        }
        state.isEditFieldsPending = false;
      },
      [EDIT_FIELDS.FAILED](state) {
        state.isEditFieldsPending = false;
      }
    },
    actions: {
      async addTag({ commit, state }, { applicationId, tag, score }) {
        try {
          commit(ADD_TAG.STARTED);
          const application = state.collection.find((application) => application.source.id === applicationId);
          if (!application) {
            throw new Error(`Unable to find application with id '${applicationId}'`);
          }
          const tags = application.tags ? application.tags.map((t) => ({ ...t })) : [];
          const existingTag = tags.findIndex((t) => t.name === tag);
          if (existingTag > -1) {
            tags[existingTag].score = score;
          } else {
            tags.push({ name: tag, score });
          }

          httpClient.put(`/api/applications/${applicationId}`, {
            tags
          });
          commit(ADD_TAG.COMPLETED, { applicationId, tag: { name: tag, score } });
        } catch (e) {
          commit(ADD_TAG.FAILED);
          throw e;
        }
      },
      async removeTag({ commit, state }, { applicationId, tag }) {
        try {
          commit(REMOVE_TAG.STARTED);
          const application = state.collection.find((application) => application.source.id === applicationId);
          if (!application) {
            throw new Error(`Unable to find application with id '${applicationId}'`);
          }
          const tags = application.tags.filter((t) => t.name !== tag.name);

          httpClient.put(`/api/applications/${applicationId}`, {
            tags
          });

          commit(REMOVE_TAG.COMPLETED, { applicationId, tag });
        } catch (e) {
          commit(REMOVE_TAG.FAILED);
          throw e;
        }
      },
      async editSummary({ commit, state }, { applicationId, fields }) {
        try {
          commit(EDIT_FIELDS.STARTED);
          const application = state.collection.find((application) => application.source.id === applicationId);
          if (!application) {
            throw new Error(`Unable to find application with id '${applicationId}'`);
          }

          httpClient.put(`/api/applications/${applicationId}`, {
            ...fields
          });

          commit(EDIT_FIELDS.COMPLETED, { applicationId, fields });
        } catch (e) {
          commit(EDIT_FIELDS.FAILED);
          throw e;
        }
      }
    }
  })(
    'portfolio',
    {
      async getById(id) {
        return httpClient.get(`/api/search/${id}`);
      },
      async getCollection({ search, portfolioId }) {
        const params = new URLSearchParams(search);

        if (portfolioId && portfolioId !== 'public') {
          params.set('portfolioId', portfolioId);
        }

        const { data, total, aggs } = await httpClient.get(`/api/search?` + params.toString());

        return {
          data,
          total,
          aggs,
          size: +params.get('size'),
          from: +params.get('from'),
          sort: params.get('sort') || ''
        };
      },
      async create({ id, ...obj }) {
        return httpClient.put(`/api/applications/${id}`, obj);
      },
      async update(id, data) {
        return httpClient.put(`/api/applications/${id}`, data);
      }
    },
    {
      import: {
        namespaced: true,
        state: {
          isImportRequestPending: false,
          isImportRequestFailed: false
        },
        mutations: {
          [IMPORT.STARTED](state) {
            state.isImportRequestPending = false;
            state.isImportRequestFailed = false;
          },
          [IMPORT.COMPLETED](state) {
            state.isImportRequestPending = false;
          },
          [IMPORT.FAILED](state) {
            state.isImportRequestPending = false;
            state.isImportRequestFailed = true;
          }
        },
        actions: {
          async csv({ commit }, { file }) {
            try {
              commit(IMPORT.STARTED);

              const response = await httpClient.post(`/api/applications/csv`, {
                csvFileData: await new Promise((resolve, reject) => {
                  const reader = new FileReader();
                  reader.readAsDataURL(file);
                  reader.onload = () => resolve(reader.result.split(',')[1]);
                  reader.onerror = (error) => reject(error);
                })
              });

              commit(IMPORT.COMPLETED);

              return response;
            } catch (e) {
              commit(IMPORT.FAILED);
            }
          }
        }
      },
      practitioners: {
        namespaced: true,
        ...cruduex('practitioners', {
          async getById(id) {
            throw new Error('Not Implemented');
          },
          async getCollection() {
            return await httpClient.get(`/api/pair-authorizator/practitioners`);
          },
          async create(obj) {
            throw new Error('Not Implemented');
          },
          async update(id, data) {
            throw new Error('Not Implemented');
          },
          async delete(id) {
            throw new Error('Not Implemented');
          }
        })
      },
      users: {
        namespaced: true,
        ...cruduex('users', {
          async getById(id) {
            return await httpClient.get(`/api/pair-authorizator/users/${id}`);
          },
          async getCollection() {
            return await httpClient.get(`/api/pair-authorizator/users`);
          },
          async create(obj) {
            throw new Error('Not Implemented');
          },
          async update(id, data) {
            return await httpClient.put(`/api/pair-authorizator/users/${id}`, data);
          },
          async delete(id) {
            throw new Error('Not Implemented');
          }
        })
      }
    }
  )
};
